import type { PreliminaryLead, PreliminaryMaturity } from '@/mocks/preliminaryLeads';

/** 企业层面资料需求（对齐 Demo CompanyMaterialRequirement） */
export type CompanyMaterialRequirement = {
  name: string;
  usage: string;
  period: string;
  missingImpact: string;
  supportedLeadIds: string[];
  supportedLeadNames: string[];
};

/** 单条线索上的推荐资料项 */
export type RecommendedMaterialItem = {
  name: string;
  usage: string;
  period: string;
  missingImpact: string;
};

/** 英文字段码 / 事件码 → 人读名（资料表专用，覆盖 event 类） */
const FIELD_CODE_CN: Record<string, string> = {
  taxable_income: '应纳税所得额',
  enterprise_type: '企业类型',
  province: '登记省份',
  eit_rate: '企业所得税适用税率',
  eit_relief: '企业所得税减免税额',
  insured_count: '从业人数',
  staff_size: '人员规模',
  registered_capital: '注册资本',
  business_scope: '经营范围',
  industry_class: '所属行业',
  taxpayer_type: '纳税人类型',
  uscc: '统一社会信用代码',
  contract_amount_gross: '合同含税金额',
  direct_holding_pct: '直接持股比例',
  industry_license_event: '行业许可事项',
  qualification_expiry_event: '资质到期与续期记录',
  scope_change_event: '经营范围变更记录',
  equity_change_event: '股权变更记录',
  charter_change_event: '章程变更记录',
  event_stage: '事项阶段',
  tax_period_start: '税款所属期起',
  tax_period_end: '税款所属期止',
  tax_period: '税款所属期',
  import_export_flag: '进出口标识',
  vat_paid: '实缴增值税',
  consumption_tax_paid: '实缴消费税',
  exempt_offset_refund: '免抵退税额',
};

/** 纯资料包代号（EQUITY-01 / GOV-01 / FIN-02 …） */
const BARE_PACK_CODE = /^[A-Z][A-Z0-9]*(?:-\d{1,4})+$/i;
/** 句中资料包代号 */
const PACK_CODE_RE =
  /\b[A-Z][A-Z0-9]*(?:-\d{1,4})+(?:\s*\/\s*[A-Z][A-Z0-9]*(?:-\d{1,4})+)*\b/gi;

/** 常见包码 → 人读资料名（裸码时回退） */
const PACK_CODE_CN: Record<string, string> = {
  'equity-01': '股权与出资资料',
  'gov-01': '工商变更与登记资料',
  'con-01': '合同与交易资料',
  'fin-01': '财务明细资料',
  'fin-02': '成本结转与核算资料',
  'invtry-01': '存货收发存资料',
  'prj-02': '产销与项目台账',
  'tax-01': '纳税申报资料',
  'corp-01': '公司主体登记资料',
  'qual-01': '资质与许可资料',
  'hr-01': '人员与用工资料',
  'asset-01': '资产明细资料',
  'perm-01': '行政许可资料',
};

function mapFieldCode(code: string): string {
  return FIELD_CODE_CN[code.trim().toLowerCase()] || '';
}

function mapPackCode(code: string): string {
  return PACK_CODE_CN[code.trim().toLowerCase()] || '';
}

/**
 * 将 v3 missing_evidence 单项规范为人读资料名。
 * 去掉字段码、EQUITY-01 等包码、LEGAL/FACT 内部引用，只留可行动的中文资料名。
 */
export function normalizeMaterialName(raw: string): string {
  let s = String(raw || '').trim();
  if (!s) return '';

  // 推理/思考噪声标记不上屏
  s = s.replace(/<\/?think>/gi, '');
  s = s.replace(/\[(?:思考|推理|内部)[^\]]*\]/g, '');
  s = s.replace(/（(?:思考|推理|内部)[^）]*）/g, '');

  // 纯包码先映射（避免后面被剥空）
  if (BARE_PACK_CODE.test(s)) {
    return mapPackCode(s) || '';
  }

  // 内部 ID / 规则引用（先剥，避免污染中文）
  s = s.replace(/\b(?:FACT|AUD|ENT|LEAD|CASE|EVT|REL)-[A-Za-z0-9]+\b/gi, '');
  s = s.replace(/\bminimum_documents\b/gi, '');
  s = s.replace(/[（(]\s*[）)]/g, '');
  s = s.replace(/规则\s*/gi, '');
  s = s.replace(/\bchanges\[\d+\](?:\.[A-Za-z_][\w]*)*/gi, '工商变更');

  // EQUITY-01 股权/出资资料 → 股权/出资资料
  const packPrefix = s.match(
    /^([A-Z][A-Z0-9]*(?:-\d{1,4})+)\s+(.+)$/i,
  );
  if (packPrefix?.[2] && /[\u4e00-\u9fff]/.test(packPrefix[2])) {
    s = packPrefix[2].trim();
  }

  // 增资验资/出资凭证（EQUITY-01）→ 增资验资/出资凭证
  s = s.replace(
    /[（(]\s*[A-Z][A-Z0-9]*(?:-\d{1,4})+(?:\s*[/／、]\s*[A-Z][A-Z0-9]*(?:-\d{1,4})+)*\s*[）)]/gi,
    '',
  );

  // 句中残留包码剥掉
  s = s.replace(PACK_CODE_RE, '');

  // 整句：code：中文说明 → 取冒号后中文
  const colonWhole = s.match(
    /^[A-Za-z][A-Za-z0-9_]*\s*[：:]\s*(.+)$/,
  );
  if (colonWhole?.[1] && /[\u4e00-\u9fff]/.test(colonWhole[1])) {
    s = colonWhole[1].trim();
  }

  // 整句：code（中文）→ 取括号中文
  const parenWhole = s.match(
    /^[A-Za-z][A-Za-z0-9_]*\s*[（(]\s*([^）)]+?)\s*[）)]\s*$/,
  );
  if (parenWhole?.[1] && /[\u4e00-\u9fff]/.test(parenWhole[1])) {
    s = parenWhole[1].trim();
  }

  // code 标准字段：中文 / code 字段：中文 / code 字段
  s = s.replace(
    /\b([A-Za-z][A-Za-z0-9_]*)\s*(?:标准)?字段\s*[：:]?\s*/gi,
    (_m, code: string) => {
      const label = mapFieldCode(code);
      return label ? `${label}：` : '';
    },
  );
  // 若变成「经营范围变更记录：」且后面为空，去掉冒号
  s = s.replace(/^([\u4e00-\u9fff]{2,20})：\s*$/, '$1');

  // 句中 code（中文）→ 中文
  s = s.replace(
    /\b([A-Za-z][A-Za-z0-9_]*)\s*[（(]([^）)]+)[）)]/g,
    (_m, code: string, inner: string) => {
      const body = inner.trim();
      if (/[\u4e00-\u9fff]/.test(body)) return body;
      return mapFieldCode(code) || body;
    },
  );

  // 句中残留 code： → 删掉英文字段码，保留后续中文说明
  s = s.replace(/\b([A-Za-z][A-Za-z0-9_]*)\s*[：:]\s*/g, '');

  // 纯字段码 / 残留裸码
  const codeOnly = s.match(/^([a-z][a-z0-9_]{2,48})$/i);
  if (codeOnly) {
    const mapped = mapFieldCode(codeOnly[1]);
    if (mapped) return mapped;
  }
  for (const [code, label] of Object.entries(FIELD_CODE_CN).sort(
    (a, b) => b[0].length - a[0].length,
  )) {
    const re = new RegExp(`(?<![A-Za-z0-9_])${code}(?![A-Za-z0-9_])`, 'gi');
    s = s.replace(re, label);
  }

  // 括号噪声：当前缺失 / 最低事实 / 闭环 / 非标准条目
  s = s.replace(
    /[（(][^）)]*(?:当前缺失|当前无该字段|最低事实|闭环|指定|非标准条目|用于\s*)[^）)]*[）)]/g,
    '',
  );
  s = s.replace(/[（(]\s*[）)]/g, '');
  // 「用于 LEGAL… / 用于闭环」尾巴
  s = s.replace(/[，,]?\s*用于\s*[A-Za-z0-9\-/,]+\s*.*$/g, '');
  s = s.replace(/[，,]?\s*用于(?:闭环|补全|判定|升级).*$/g, '');
  s = s.replace(/由\s*[A-Z]\s*向\s*[A-Z]\s*升级/g, '');
  s = s.replace(/由\s*miss\s*转为可判定/gi, '');
  s = s.replace(/[，,]?\s*本轮缺失\s*$/g, '');

  s = s.replace(/\s{2,}/g, ' ').trim();
  s = s.replace(/^[、，,；;：:\s]+|[、，,；;：:\s]+$/g, '').trim();
  // 「字段」单独残留
  if (/^(?:标准)?字段$/.test(s)) return '';

  // 表格展示不宜过长
  if (s.length > 42) {
    s = `${s.slice(0, 42).replace(/[、，,\s：:]+$/g, '')}…`;
  }
  return s;
}

/** O01/事实账本字段名：不是可上传的「资料件」，不上资料需求表 */
const FACT_FIELD_ONLY =
  /^(应纳税所得额|所属行业|库存数量|单位成本|实际成本|资产类型|直接持股比例|明细项编号|明细项名称|经营范围|注册资本|从业人数|人员规模|纳税人类型|统一社会信用代码|企业类型|登记省份|成立日期|主题编号|查询依据|事项阶段)$/;

/** Demo 口径：资料名应像「协议/报表/台账」等可上传件 */
function isDocumentLikeName(name: string): boolean {
  return /表|协议|决议|清单|凭证|台账|报表|合同|证明|说明|记录|底稿|申报|发票|账册|章程|评估|许可|年报|执照|明细|资料|公文|通知|函|证书|备案/.test(
    name,
  );
}

function isUsefulMaterialName(name: string): boolean {
  if (!name || name.length < 2) return false;
  if (BARE_PACK_CODE.test(name)) return false;
  if (name === '相关资料项' || name === '相关资料') return false;
  if (/^(?:标准)?字段$/.test(name)) return false;
  if (FACT_FIELD_ONLY.test(name)) return false;
  // 过短且无中文：跳过
  if (name.length < 4 && !/[\u4e00-\u9fff]/.test(name)) return false;
  // 纯英文短码
  if (/^[A-Za-z0-9_./-]{1,24}$/.test(name) && !/[\u4e00-\u9fff]/.test(name)) {
    return false;
  }
  // 仍含大量 snake_case 则丢弃
  if (/[a-z]+_[a-z]+/i.test(name)) return false;
  if (!/[\u4e00-\u9fff]/.test(name)) return false;
  // 优先文档型名称；纯字段短语（无资料件语义）不进表
  if (!isDocumentLikeName(name) && name.length <= 8) return false;
  return true;
}

/** 企业表展示上限（对齐 Demo：每户十几项内，而非 O01 百级缺口） */
const MAX_COMPANY_MATERIALS = 12;
/** 单条线索写入推荐资料上限（Demo 常见 3–4 项） */
const MAX_LEAD_MATERIALS = 4;

/** 归一化合并键：去掉期间前缀数字差异，保留主干 */
function materialMergeKey(name: string): string {
  return name
    .replace(/20\d{2}\s*年?/g, '')
    .replace(/[（(][^）)]*[）)]/g, '')
    .replace(/[…\.]+$/g, '')
    .replace(/\s+/g, '')
    .slice(0, 40)
    .toLowerCase();
}

/** 对齐 Demo unifiedLeadService 缺失影响文案 */
function impactOf(maturity: PreliminaryMaturity): string {
  if (maturity === 'A' || maturity === 'B') {
    return '核心资料，缺失将无法完成核验与测算';
  }
  return '辅助资料，缺失会影响核验的完整性';
}

function elevateImpact(a: string, b: string): string {
  const rank = (s: string) => (s.startsWith('核心') ? 2 : 1);
  return rank(b) > rank(a) ? b : a;
}

/**
 * 从线索 missingConditions / verificationGaps 合并去重企业资料需求。
 * 原料对齐方案：v3 leads.missing_evidence（经转换写入 missingConditions）。
 */
export function buildCompanyMaterialRequirements(
  leads: PreliminaryLead[],
): CompanyMaterialRequirement[] {
  const map = new Map<string, CompanyMaterialRequirement>();

  for (const lead of leads) {
    const impact = impactOf(lead.maturityLevel);
    const materials =
      lead.recommendedMaterials?.length
        ? lead.recommendedMaterials
        : (lead.detail?.missingConditions ?? []).map((name) => ({
            name,
            usage: '用于核验本线索的待确认事实与口径',
            period: '当期',
            missingImpact: impact,
          }));

    // 无 missing 时用待核验问题兜底，避免整表为空
    const rows =
      materials.length > 0
        ? materials
        : (lead.verificationGaps ?? []).slice(0, 3).map((g) => ({
            name: g.length > 36 ? `${g.slice(0, 36)}…相关资料` : `${g}相关资料`,
            usage: '用于核验本线索的待确认事实与口径',
            period: '当期',
            missingImpact: impact,
          }));

    const seenLocal = new Set<string>();
    for (const row of rows) {
      const name = normalizeMaterialName(row.name);
      if (!isUsefulMaterialName(name)) continue;
      const key = materialMergeKey(name);
      if (!key || seenLocal.has(key)) continue;
      seenLocal.add(key);

      const period = row.period || '当期';
      const usage = row.usage || '用于核验本线索的待确认事实与口径';
      const rowImpact = row.missingImpact || impact;

      const existing = map.get(key);
      if (existing) {
        if (!existing.supportedLeadIds.includes(lead.id)) {
          existing.supportedLeadIds.push(lead.id);
          existing.supportedLeadNames.push(lead.previewTitle);
          existing.missingImpact = elevateImpact(
            existing.missingImpact,
            rowImpact,
          );
        }
        continue;
      }

      map.set(key, {
        name,
        usage,
        period,
        missingImpact: rowImpact,
        supportedLeadIds: [lead.id],
        supportedLeadNames: [lead.previewTitle],
      });
    }
  }

  // 多线索支持的排前，再按影响；截断到 Demo 可读体量
  return Array.from(map.values())
    .sort((a, b) => {
      const byLeads = b.supportedLeadIds.length - a.supportedLeadIds.length;
      if (byLeads !== 0) return byLeads;
      const doc = Number(isDocumentLikeName(b.name)) - Number(isDocumentLikeName(a.name));
      if (doc !== 0) return doc;
      const rank = (s: string) =>
        s.startsWith('核心') ? 0 : s.startsWith('重要') ? 1 : 2;
      return rank(a.missingImpact) - rank(b.missingImpact);
    })
    .slice(0, MAX_COMPANY_MATERIALS);
}

/** 从 missing_evidence 原始列表生成推荐资料（写入单条线索） */
export function materialsFromMissingEvidence(
  missing: string[] | undefined,
  _leadTitle: string,
  _period: string,
  maturity: PreliminaryMaturity,
): RecommendedMaterialItem[] {
  const impact = impactOf(maturity);
  const out: RecommendedMaterialItem[] = [];
  const seen = new Set<string>();
  for (const raw of missing ?? []) {
    const name = normalizeMaterialName(raw);
    if (!isUsefulMaterialName(name)) continue;
    const key = materialMergeKey(name);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      name,
      usage: '用于核验本线索的待确认事实与口径',
      period: '当期',
      missingImpact: impact,
    });
    if (out.length >= MAX_LEAD_MATERIALS) break;
  }
  return out;
}
